import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Topbar from "@/components/layout/Topbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatCurrency, formatShortDate, formatDate } from "@/lib/format";
import {
    BarChart, Bar, AreaChart, Area, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
    BarChart3, Calendar, DollarSign, Receipt, Users, Globe2,
    Sparkles, BedDouble, XCircle, UserX, FileSpreadsheet, FileDown,
    FileText, RefreshCw, Filter, Calculator, Wallet, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

// ─────────────────────────────────────────────────────────────
// Report registry — 15 reports across 4 categories
// ─────────────────────────────────────────────────────────────
const CATEGORIES = [
    { key: "operations" },
    { key: "financial" },
    { key: "channels" },
    { key: "guests" },
];

const REPORTS = [
    { key: "occupancy",           cat: "operations", icon: BedDouble,    filters: ["dates", "property", "room_type"] },
    { key: "adr-revpar",          cat: "operations", icon: TrendingUp,   filters: ["dates", "property", "room_type"] },
    { key: "housekeeping",        cat: "operations", icon: Sparkles,     filters: ["dates", "staff"] },
    { key: "reservations",        cat: "operations", icon: Calendar,     filters: ["dates", "property", "room_type", "channel", "payment_method"] },

    { key: "revenue",             cat: "financial",  icon: DollarSign,   filters: ["dates", "property", "room_type", "channel"] },
    { key: "payments",            cat: "financial",  icon: Wallet,       filters: ["dates", "payment_method"] },
    { key: "pos-sales",           cat: "financial",  icon: Receipt,      filters: ["dates", "payment_method"] },
    { key: "monthly-summary",     cat: "financial",  icon: Calculator,   filters: ["months", "property"] },

    { key: "channel-performance", cat: "channels",   icon: Globe2,       filters: ["dates", "property"] },
    { key: "direct-performance",  cat: "channels",   icon: TrendingUp,   filters: ["dates", "property"] },
    { key: "ota-commission",      cat: "channels",   icon: BarChart3,    filters: ["dates", "property"] },

    { key: "guests",              cat: "guests",     icon: Users,        filters: ["dates"] },
    { key: "cancellations",       cat: "guests",     icon: XCircle,      filters: ["dates", "property"] },
    { key: "no-shows",            cat: "guests",     icon: UserX,        filters: ["dates", "property"] },
];

function defaultRange(days = 30) {
    const d = new Date();
    const from = new Date(d); from.setDate(d.getDate() - days);
    return { date_from: from.toISOString().slice(0, 10), date_to: d.toISOString().slice(0, 10) };
}

const PIE_COLORS = ["#ff7b02", "#2563EB", "#F59E0B", "#DC2626", "#7C3AED", "#0891B2", "#65A30D", "#DB2777"];

export default function Reports() {
    const { t } = useTranslation();
    const [cat, setCat] = useState("operations");
    const [active, setActive] = useState("occupancy");
    const [filters, setFilters] = useState({ ...defaultRange(30), months: 12 });
    const [options, setOptions] = useState({ properties: [], room_types: [], channels: [], staff: [], payment_methods: [] });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const report = useMemo(() => REPORTS.find((r) => r.key === active), [active]);

    useEffect(() => {
        api.get("/reports/filter-options").then((r) => setOptions(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true);
            try {
                const params = buildParams(report, filters);
                const { data } = await api.get(`/reports/${active}?${params}`);
                if (!cancelled) setData(data);
            } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("reports.load_failed"));
                if (!cancelled) setData(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => { cancelled = true; };
    }, [active, filters, report]);

    const exportReport = async (format) => {
        try {
            const params = buildParams(report, filters) + `&format=${format}`;
            const base = process.env.REACT_APP_BACKEND_URL;
            const token = localStorage.getItem("akm_token");
            const res = await fetch(`${base}/api/reports/${active}?${params}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(`Export failed (${res.status})`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `akomoda-${active}-${new Date().toISOString().slice(0, 10)}.${format}`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success(t("reports.downloaded", { format: format.toUpperCase() }));
        } catch (e) {
            toast.error(e.message);
        }
    };

    const refresh = () => setFilters({ ...filters });
    const reportsInCat = REPORTS.filter((r) => r.cat === cat);

    return (
        <>
            <Topbar title={t("reports.title")} subtitle={t("reports.subtitle")} />
            <div className="flex-1 overflow-auto">
                <div className="flex flex-col lg:flex-row" data-testid="reports-shell">

                    {/* Left rail: category + report picker */}
                    <aside className="border-b border-border lg:w-[260px] lg:border-b-0 lg:border-r">
                        <div className="flex gap-0.5 px-3 pt-3 lg:flex-col lg:gap-0">
                            {CATEGORIES.map((c) => (
                                <button key={c.key} onClick={() => { setCat(c.key); setActive(REPORTS.find((r) => r.cat === c.key).key); }}
                                        data-testid={`reports-cat-${c.key}`}
                                        className={`flex-1 lg:flex-none rounded-md px-2 py-1.5 text-left text-[12px] font-semibold uppercase tracking-wider transition ${
                                            cat === c.key ? "text-[#ff7b02]" : "text-muted-foreground hover:text-foreground"
                                        }`}>
                                    {t(`reports.categories.${c.key}`)}
                                </button>
                            ))}
                        </div>
                        <ul className="flex gap-1 overflow-x-auto px-3 pb-3 lg:flex-col lg:gap-0.5 lg:pb-6">
                            {reportsInCat.map((r) => {
                                const Icon = r.icon;
                                const isActive = active === r.key;
                                return (
                                    <li key={r.key}>
                                        <button onClick={() => setActive(r.key)}
                                                data-testid={`reports-link-${r.key}`}
                                                className={`flex w-full items-center gap-2 whitespace-nowrap rounded-md px-2.5 py-2 text-[13px] transition ${
                                                    isActive ? "bg-[#ff7b02]/10 font-medium text-[#ff7b02]" : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                                                }`}>
                                            <Icon size={14} className="shrink-0" />
                                            <span className="truncate">{t(`reports.list.${r.key}`)}</span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </aside>

                    {/* Main panel */}
                    <main className="flex-1 p-4 md:p-6">
                        <FilterBar report={report} filters={filters} setFilters={setFilters} options={options} />

                        <div className="mb-3 mt-3 flex flex-wrap items-center gap-2">
                            <div>
                                <h2 className="text-base font-semibold tracking-tight" data-testid="report-title">{report ? t(`reports.list.${report.key}`) : ""}</h2>
                                <p className="mt-0.5 text-[11px] text-muted-foreground">
                                    {report?.filters?.includes("months")
                                        ? t("reports.last_n_months", { months: filters.months })
                                        : `${formatDate(filters.date_from)} → ${formatDate(filters.date_to)}`}
                                </p>
                            </div>
                            <div className="ml-auto flex flex-wrap items-center gap-1.5">
                                <button onClick={refresh} className="akm-btn-ghost text-[11px]" data-testid="report-refresh">
                                    <RefreshCw size={12} /> {t("reports.refresh")}
                                </button>
                                <button onClick={() => exportReport("csv")} className="akm-btn-ghost text-[11px]" data-testid="export-csv">
                                    <FileDown size={12} /> CSV
                                </button>
                                <button onClick={() => exportReport("xlsx")} className="akm-btn-ghost text-[11px]" data-testid="export-xlsx">
                                    <FileSpreadsheet size={12} /> Excel
                                </button>
                                <button onClick={() => exportReport("pdf")} className="akm-btn-primary text-[11px]" data-testid="export-pdf">
                                    <FileText size={12} /> PDF
                                </button>
                            </div>
                        </div>

                        {loading && <div className="py-12 text-center text-sm text-muted-foreground" data-testid="report-loading">{t("reports.loading")}</div>}

                        {!loading && data && (
                            <ReportBody report={report} data={data} />
                        )}
                    </main>
                </div>
            </div>
        </>
    );
}

// ─────────────────────────────────────────────────────────────
// Filter bar — adapts to report.filters
// ─────────────────────────────────────────────────────────────
function FilterBar({ report, filters, setFilters, options }) {
    const { t } = useTranslation();
    if (!report) return null;
    const has = (k) => report.filters.includes(k);
    return (
        <div className="akm-card flex flex-wrap items-end gap-3 p-3" data-testid="report-filter-bar">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <Filter size={12} /> {t("reports.filters")}
            </div>
            {has("dates") && (
                <>
                    <FilterField label={t("reports.from")}>
                        <input type="date" value={filters.date_from || ""}
                               onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                               data-testid="filter-date-from"
                               className="rounded-md border border-border bg-background px-2 py-1 text-[12px] outline-none focus:border-[#ff7b02]" />
                    </FilterField>
                    <FilterField label={t("reports.to")}>
                        <input type="date" value={filters.date_to || ""}
                               onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                               data-testid="filter-date-to"
                               className="rounded-md border border-border bg-background px-2 py-1 text-[12px] outline-none focus:border-[#ff7b02]" />
                    </FilterField>
                </>
            )}
            {has("months") && (
                <FilterField label={t("reports.months")}>
                    <select value={filters.months || 12}
                            onChange={(e) => setFilters({ ...filters, months: parseInt(e.target.value, 10) })}
                            data-testid="filter-months"
                            className="rounded-md border border-border bg-background px-2 py-1 text-[12px] outline-none">
                        {[3, 6, 12, 18, 24].map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                </FilterField>
            )}
            {has("property") && options.properties?.length > 0 && (
                <FilterField label={t("reports.property")}>
                    <select value={filters.property_id || ""}
                            onChange={(e) => setFilters({ ...filters, property_id: e.target.value || undefined })}
                            data-testid="filter-property"
                            className="rounded-md border border-border bg-background px-2 py-1 text-[12px] outline-none">
                        <option value="">{t("reports.all")}</option>
                        {options.properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                </FilterField>
            )}
            {has("room_type") && options.room_types?.length > 0 && (
                <FilterField label={t("reports.room_type")}>
                    <select value={filters.room_type || ""}
                            onChange={(e) => setFilters({ ...filters, room_type: e.target.value || undefined })}
                            data-testid="filter-room-type"
                            className="rounded-md border border-border bg-background px-2 py-1 text-[12px] outline-none">
                        <option value="">{t("reports.all")}</option>
                        {options.room_types.map((t2) => <option key={t2} value={t2}>{t2}</option>)}
                    </select>
                </FilterField>
            )}
            {has("channel") && options.channels?.length > 0 && (
                <FilterField label={t("reports.channel")}>
                    <select value={filters.channel || ""}
                            onChange={(e) => setFilters({ ...filters, channel: e.target.value || undefined })}
                            data-testid="filter-channel"
                            className="rounded-md border border-border bg-background px-2 py-1 text-[12px] outline-none">
                        <option value="">{t("reports.all")}</option>
                        {options.channels.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </FilterField>
            )}
            {has("staff") && options.staff?.length > 0 && (
                <FilterField label={t("reports.staff")}>
                    <select value={filters.staff_id || ""}
                            onChange={(e) => setFilters({ ...filters, staff_id: e.target.value || undefined })}
                            data-testid="filter-staff"
                            className="rounded-md border border-border bg-background px-2 py-1 text-[12px] outline-none">
                        <option value="">{t("reports.all")}</option>
                        {options.staff.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
                    </select>
                </FilterField>
            )}
            {has("payment_method") && options.payment_methods?.length > 0 && (
                <FilterField label={t("reports.payment_method")}>
                    <select value={filters.payment_method || ""}
                            onChange={(e) => setFilters({ ...filters, payment_method: e.target.value || undefined })}
                            data-testid="filter-payment-method"
                            className="rounded-md border border-border bg-background px-2 py-1 text-[12px] outline-none capitalize">
                        <option value="">{t("reports.all")}</option>
                        {options.payment_methods.map((m) => <option key={m} value={m}>{m.replace("_", " ")}</option>)}
                    </select>
                </FilterField>
            )}
        </div>
    );
}

function FilterField({ label, children }) {
    return (
        <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
            {children}
        </label>
    );
}

function buildParams(report, filters) {
    const p = new URLSearchParams();
    if (!report) return "";
    const has = (k) => report.filters.includes(k);
    if (has("dates")) { if (filters.date_from) p.set("date_from", filters.date_from); if (filters.date_to) p.set("date_to", filters.date_to); }
    if (has("months") && filters.months) p.set("months", filters.months);
    if (has("property") && filters.property_id) p.set("property_id", filters.property_id);
    if (has("room_type") && filters.room_type) p.set("room_type", filters.room_type);
    if (has("channel") && filters.channel) p.set("channel", filters.channel);
    if (has("staff") && filters.staff_id) p.set("staff_id", filters.staff_id);
    if (has("payment_method") && filters.payment_method) p.set("payment_method", filters.payment_method);
    return p.toString();
}

// ─────────────────────────────────────────────────────────────
// Report body — dispatches to specific rendering
// ─────────────────────────────────────────────────────────────
function ReportBody({ report, data }) {
    if (!data) return null;
    return (
        <div className="space-y-4">
            <SummaryStrip summary={data.summary} />
            {report.key === "occupancy"           && <OccupancyView data={data} />}
            {report.key === "adr-revpar"          && <ADRRevPARView data={data} />}
            {report.key === "revenue"             && <RevenueView data={data} />}
            {report.key === "reservations"        && <TableView rows={data.rows} columns={[
                ["confirmation_code","Code"],["guest","Guest"],["check_in","Check-in"],["check_out","Check-out"],
                ["nights","Nights"],["room","Room"],["room_type","Type"],["source","Source"],["status","Status"],
                ["payment_status","Payment"],["total","Total","money"],["paid","Paid","money"],
            ]} />}
            {report.key === "channel-performance" && <ChannelPerfView data={data} />}
            {report.key === "direct-performance"  && <DirectPerfView data={data} />}
            {report.key === "ota-commission"      && <OTACommissionView data={data} />}
            {report.key === "payments"            && <PaymentsReportView data={data} />}
            {report.key === "housekeeping"        && <HousekeepingView data={data} />}
            {report.key === "pos-sales"           && <POSReportView data={data} />}
            {report.key === "guests"              && <GuestsReportView data={data} />}
            {report.key === "cancellations"       && <CancellationsView data={data} />}
            {report.key === "no-shows"            && <NoShowsView data={data} />}
            {report.key === "monthly-summary"     && <MonthlySummaryView data={data} />}
        </div>
    );
}

function SummaryStrip({ summary }) {
    const { t } = useTranslation();
    if (!summary) return null;
    const entries = Object.entries(summary);
    const tr = (k) => {
        const norm = k.toLowerCase()
            .replace(/[%.]/g, "")
            .trim()
            .replace(/\s+/g, "_")
            .replace(/_+$/g, "");
        const out = t(`reports.summary.${norm}`, { defaultValue: "" });
        return out || k;
    };
    return (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6" data-testid="report-summary">
            {entries.map(([k, v]) => (
                <div key={k} className="akm-card p-3">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{tr(k)}</div>
                    <div className="mt-0.5 text-[15px] font-semibold tracking-tight">
                        {typeof v === "number" ? (k.includes("%") ? `${v}%` : (k.toLowerCase().includes("revenue") || k.toLowerCase().includes("paid") || k.toLowerCase().includes("commission") || k.toLowerCase().includes("received") || k.toLowerCase().includes("refund") || k.toLowerCase().includes("value")) ? formatCurrency(v) : v) : v}
                    </div>
                </div>
            ))}
        </div>
    );
}

// ─────── Generic reusable table ───────
function TableView({ rows, columns, emptyText }) {
    const { t } = useTranslation();
    const labelFor = (key, fallback) => {
        const tx = t(`reports.cols.${key}`, { defaultValue: "" });
        return tx || fallback;
    };
    const empty = emptyText || t("reports.no_data");
    return (
        <div className="akm-card overflow-hidden" data-testid="report-table">
            <div className="max-h-[560px] overflow-auto">
                <table className="w-full text-[13px]">
                    <thead className="sticky top-0 bg-muted/50 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>{columns.map(([k, label]) => <th key={label} className="px-3 py-2 text-left">{labelFor(k, label)}</th>)}</tr>
                    </thead>
                    <tbody>
                        {rows?.map((r, i) => (
                            <tr key={i} className="border-t border-border hover:bg-muted/30">
                                {columns.map(([k, _, kind]) => {
                                    const raw = r[k];
                                    let cell;
                                    if (kind === "money") cell = formatCurrency(raw || 0);
                                    else if (typeof raw === "number") cell = raw.toLocaleString();
                                    // Date/month strings from backend arrive as ISO ("2026-08-29" or
                                    // "2026-08") — render them in DD/MM/YYYY (or MM/YYYY) so users
                                    // never see US/ISO leaks in report tables. Detects the shape
                                    // by regex so we don't have to touch every report column def.
                                    else if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
                                        cell = formatDate(raw);
                                    } else if (typeof raw === "string" && /^\d{4}-\d{2}$/.test(raw)) {
                                        const [yy, mm] = raw.split("-");
                                        cell = `${mm}/${yy}`;
                                    } else cell = String(raw ?? "-");
                                    return (
                                        <td key={k} className={`px-3 py-1.5 ${kind === "money" ? "text-right font-medium" : ""}`}>{cell}</td>
                                    );
                                })}
                            </tr>
                        ))}
                        {(!rows || rows.length === 0) && (
                            <tr><td colSpan={columns.length} className="py-10 text-center text-muted-foreground">{empty}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─────── Per-report visualizations ───────
function ChartCard({ title, children, height = 260 }) {
    return (
        <div className="akm-card p-4">
            <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{title}</div>
            <div style={{ height }}>
                <ResponsiveContainer>{children}</ResponsiveContainer>
            </div>
        </div>
    );
}

function OccupancyView({ data }) {
    const { t } = useTranslation();
    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <ChartCard title={t("reports.charts.daily_occupancy")}>
                <AreaChart data={data.rows} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                    <defs><linearGradient id="occ" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ff7b02" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#ff7b02" stopOpacity={0} />
                    </linearGradient></defs>
                    <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 4" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={(d) => formatShortDate(d)} tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} width={36} domain={[0, 100]} />
                    <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => `${v}%`} labelFormatter={formatDate} />
                    <Area type="monotone" dataKey="occupancy_pct" stroke="#ff7b02" strokeWidth={2} fill="url(#occ)" />
                </AreaChart>
            </ChartCard>
            <ChartCard title={t("reports.charts.adr_revpar")}>
                <LineChart data={data.rows} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                    <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 4" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={(d) => formatShortDate(d)} tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} width={48} />
                    <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => formatCurrency(v)} labelFormatter={formatDate} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="adr" stroke="#ff7b02" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="revpar" stroke="#2563EB" strokeWidth={2} dot={false} />
                </LineChart>
            </ChartCard>
            <div className="lg:col-span-2">
                <TableView rows={data.rows} columns={[
                    ["date","Date"],["available_rooms","Rooms"],["sold_nights","Sold"],
                    ["occupancy_pct","Occupancy %"],["revenue","Revenue","money"],
                    ["adr","ADR","money"],["revpar","RevPAR","money"],
                ]} />
            </div>
        </div>
    );
}

function ADRRevPARView({ data }) {
    const { t } = useTranslation();
    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <ChartCard title={t("reports.charts.adr_revpar_daily")}>
                <LineChart data={data.rows} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                    <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 4" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={(d) => formatShortDate(d)} tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} width={48} />
                    <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => formatCurrency(v)} labelFormatter={formatDate} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="adr" stroke="#ff7b02" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="revpar" stroke="#2563EB" strokeWidth={2} dot={false} />
                </LineChart>
            </ChartCard>
            <ChartCard title={t("reports.charts.occupancy_daily")}>
                <BarChart data={data.rows} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                    <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 4" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={(d) => formatShortDate(d)} tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} width={36} domain={[0, 100]} />
                    <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => `${v}%`} labelFormatter={formatDate} />
                    <Bar dataKey="occupancy_pct" fill="#ff7b02" radius={[3, 3, 0, 0]} />
                </BarChart>
            </ChartCard>
            <div className="lg:col-span-2">
                <TableView rows={data.rows} columns={[
                    ["date","Date"],["rooms_available","Rooms"],["rooms_sold","Sold"],
                    ["occupancy_pct","Occ %"],["revenue","Revenue","money"],
                    ["adr","ADR","money"],["revpar","RevPAR","money"],
                ]} />
            </div>
        </div>
    );
}

function RevenueView({ data }) {
    const { t } = useTranslation();
    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <ChartCard title={t("reports.charts.daily_revenue")}>
                <AreaChart data={data.series} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                    <defs><linearGradient id="rev2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ff7b02" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#ff7b02" stopOpacity={0} />
                    </linearGradient></defs>
                    <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 4" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={(d) => formatShortDate(d)} tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} width={56} />
                    <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => formatCurrency(v)} labelFormatter={formatDate} />
                    <Area type="monotone" dataKey="revenue" stroke="#ff7b02" strokeWidth={2} fill="url(#rev2)" />
                </AreaChart>
            </ChartCard>
            <ChartCard title={t("reports.charts.revenue_by_source")}>
                <PieChart>
                    <Pie data={data.rows} dataKey="revenue" nameKey="source" outerRadius={84}>
                        {data.rows?.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => formatCurrency(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
            </ChartCard>
            <div className="lg:col-span-2">
                <TableView rows={data.rows} columns={[
                    ["source","Source"],["bookings","Bookings"],["nights","Nights"],
                    ["revenue","Revenue","money"],["share_pct","Share %"],["adr","ADR","money"],
                ]} />
            </div>
            {data.by_room_type?.length > 0 && (
                <div className="lg:col-span-2">
                    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("reports.cols.by_room_type")}</div>
                    <TableView rows={data.by_room_type} columns={[
                        ["room_type","Type"],["bookings","Bookings"],["nights","Nights"],
                        ["revenue","Revenue","money"],["adr","ADR","money"],
                    ]} />
                </div>
            )}
        </div>
    );
}

function ChannelPerfView({ data }) {
    const { t } = useTranslation();
    return (
        <div className="space-y-4">
            <ChartCard title={t("reports.charts.revenue_per_channel")} height={300}>
                <BarChart data={data.rows} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                    <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 4" vertical={false} />
                    <XAxis dataKey="channel" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} width={56} />
                    <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => formatCurrency(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="revenue" name="Gross" fill="#ff7b02" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="net_revenue" name="Net" fill="#2563EB" radius={[3, 3, 0, 0]} />
                </BarChart>
            </ChartCard>
            <TableView rows={data.rows} columns={[
                ["channel","Channel"],["bookings","Bookings"],["nights","Nights"],
                ["guests","Guests"],["revenue","Gross","money"],["share_pct","Share %"],
                ["adr","ADR","money"],["commission_pct","Comm %"],
                ["estimated_commission","Est. commission","money"],
                ["net_revenue","Net revenue","money"],
            ]} />
        </div>
    );
}

function DirectPerfView({ data }) {
    const { t } = useTranslation();
    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <ChartCard title={t("reports.charts.direct_vs_ota")} height={260}>
                <PieChart>
                    <Pie data={data.rows} dataKey="revenue" nameKey="category" outerRadius={84}>
                        {data.rows?.map((_, i) => <Cell key={i} fill={i === 0 ? "#ff7b02" : "#94A3B8"} />)}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => formatCurrency(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
            </ChartCard>
            <div>
                <TableView rows={data.rows} columns={[
                    ["category","Category"],["bookings","Bookings"],["nights","Nights"],
                    ["revenue","Revenue","money"],["share_pct","Share %"],
                    ["estimated_savings","Comm saved","money"],
                ]} />
            </div>
            {data.direct_sources?.length > 0 && (
                <div className="lg:col-span-2">
                    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("reports.cols.direct_breakdown")}</div>
                    <TableView rows={data.direct_sources} columns={[
                        ["source","Source"],["bookings","Bookings"],["revenue","Revenue","money"],
                    ]} />
                </div>
            )}
        </div>
    );
}

function OTACommissionView({ data }) {
    const { t } = useTranslation();
    return (
        <div className="space-y-4">
            <ChartCard title={t("reports.charts.commission_per_ota")}>
                <BarChart data={data.rows} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                    <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 4" vertical={false} />
                    <XAxis dataKey="channel" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} width={56} />
                    <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => formatCurrency(v)} />
                    <Bar dataKey="commission_amount" fill="#DC2626" radius={[3, 3, 0, 0]} />
                </BarChart>
            </ChartCard>
            <TableView rows={data.rows} columns={[
                ["channel","OTA"],["bookings","Bookings"],
                ["gross_revenue","Gross","money"],["commission_pct","Rate %"],
                ["commission_amount","Commission","money"],["net_revenue","Net","money"],
            ]} />
        </div>
    );
}

function PaymentsReportView({ data }) {
    const { t } = useTranslation();
    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <ChartCard title={t("reports.charts.daily_collections")}>
                <AreaChart data={data.series} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                    <defs><linearGradient id="pay" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2563EB" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                    </linearGradient></defs>
                    <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 4" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={(d) => formatShortDate(d)} tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} width={56} />
                    <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => formatCurrency(v)} labelFormatter={formatDate} />
                    <Area type="monotone" dataKey="amount" stroke="#2563EB" strokeWidth={2} fill="url(#pay)" />
                </AreaChart>
            </ChartCard>
            <ChartCard title={t("reports.charts.by_payment_method")}>
                <PieChart>
                    <Pie data={data.rows} dataKey="amount" nameKey="method" outerRadius={84}>
                        {data.rows?.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => formatCurrency(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
            </ChartCard>
            <div className="lg:col-span-2">
                <TableView rows={data.rows} columns={[
                    ["method","Method"],["count","Count"],
                    ["amount","Amount","money"],["share_pct","Share %"],
                ]} />
            </div>
        </div>
    );
}

function HousekeepingView({ data }) {
    const { t } = useTranslation();
    return (
        <div className="space-y-4">
            <div className="akm-card p-3">
                <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{t("reports.cols.by_status")}</div>
                <div className="mt-2 grid grid-cols-2 gap-2 md:grid-cols-5">
                    {Object.entries(data.by_status || {}).map(([k, v]) => (
                        <div key={k} className="rounded-md border border-border bg-background p-2">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t(`reports.cols.${k}`, { defaultValue: k })}</div>
                            <div className="mt-0.5 text-lg font-semibold">{v}</div>
                        </div>
                    ))}
                </div>
            </div>
            <TableView rows={data.rows} columns={[
                ["assignee","Assignee"],["total","Total"],["done","Done"],
                ["in_progress","In progress"],["pending","Pending"],
                ["completion_pct","Completion %"],["avg_minutes","Avg min"],
            ]} />
        </div>
    );
}

function POSReportView({ data }) {
    const { t } = useTranslation();
    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <ChartCard title={t("reports.charts.revenue_by_outlet")}>
                <BarChart data={data.rows} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                    <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 4" vertical={false} />
                    <XAxis dataKey="outlet" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} width={56} />
                    <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => formatCurrency(v)} />
                    <Bar dataKey="revenue" fill="#ff7b02" radius={[3, 3, 0, 0]} />
                </BarChart>
            </ChartCard>
            <ChartCard title={t("reports.charts.by_payment_method")}>
                <PieChart>
                    <Pie data={data.by_payment} dataKey="amount" nameKey="method" outerRadius={84}>
                        {data.by_payment?.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => formatCurrency(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
            </ChartCard>
            <div className="lg:col-span-2">
                <TableView rows={data.rows} columns={[
                    ["outlet","Outlet"],["orders","Orders"],["items","Items"],
                    ["revenue","Revenue","money"],["avg_ticket","Avg ticket","money"],
                ]} />
            </div>
            {data.top_products?.length > 0 && (
                <div className="lg:col-span-2">
                    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("reports.cols.top_products")}</div>
                    <TableView rows={data.top_products} columns={[
                        ["product","Product"],["quantity","Quantity"],["revenue","Revenue","money"],
                    ]} />
                </div>
            )}
        </div>
    );
}

function GuestsReportView({ data }) {
    const { t } = useTranslation();
    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <ChartCard title={t("reports.charts.by_segment")}>
                <PieChart>
                    <Pie data={data.rows} dataKey="guests" nameKey="segment" outerRadius={84}>
                        {data.rows?.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
            </ChartCard>
            <ChartCard title={t("reports.charts.by_loyalty_tier")}>
                <BarChart data={data.by_tier} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                    <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 4" vertical={false} />
                    <XAxis dataKey="tier" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} width={32} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Bar dataKey="guests" fill="#ff7b02" radius={[3, 3, 0, 0]} />
                </BarChart>
            </ChartCard>
            <TableView rows={data.rows} columns={[
                ["segment","Segment"],["guests","Guests"],["share_pct","Share %"],
            ]} />
            <TableView rows={data.by_country} columns={[
                ["country","Country"],["guests","Guests"],
            ]} emptyText={t("reports.cols.no_country")} />
        </div>
    );
}

function CancellationsView({ data }) {
    const { t } = useTranslation();
    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {data.by_source?.length > 0 && (
                <ChartCard title={t("reports.charts.lost_revenue")}>
                    <BarChart data={data.by_source} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                        <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 4" vertical={false} />
                        <XAxis dataKey="source" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} width={56} />
                        <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => formatCurrency(v)} />
                        <Bar dataKey="lost_revenue" fill="#DC2626" radius={[3, 3, 0, 0]} />
                    </BarChart>
                </ChartCard>
            )}
            <div className={data.by_source?.length > 0 ? "" : "lg:col-span-2"}>
                <TableView rows={data.rows} columns={[
                    ["confirmation_code","Code"],["guest","Guest"],
                    ["check_in","Check-in"],["check_out","Check-out"],["source","Source"],
                    ["lost_revenue","Lost","money"],["cancelled_at","Cancelled"],["reason","Reason"],
                ]} />
            </div>
        </div>
    );
}

function NoShowsView({ data }) {
    const { t } = useTranslation();
    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {data.by_source?.length > 0 && (
                <ChartCard title={t("reports.charts.noshow_by_source")}>
                    <BarChart data={data.by_source} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                        <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 4" vertical={false} />
                        <XAxis dataKey="source" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} width={56} />
                        <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => formatCurrency(v)} />
                        <Bar dataKey="revenue" fill="#F59E0B" radius={[3, 3, 0, 0]} />
                    </BarChart>
                </ChartCard>
            )}
            <div className={data.by_source?.length > 0 ? "" : "lg:col-span-2"}>
                <TableView rows={data.rows} columns={[
                    ["confirmation_code","Code"],["guest","Guest"],
                    ["check_in","Check-in"],["source","Source"],
                    ["amount","Amount","money"],["paid","Paid","money"],
                ]} />
            </div>
        </div>
    );
}

function MonthlySummaryView({ data }) {
    const { t } = useTranslation();
    return (
        <div className="space-y-4">
            <ChartCard title={t("reports.charts.monthly_totals")} height={300}>
                <BarChart data={data.rows} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                    <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 4" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} width={56} />
                    <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => formatCurrency(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="total_revenue" name="Total revenue" fill="#ff7b02" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="net_revenue_after_commission" name="Net (post commission)" fill="#2563EB" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="estimated_ota_commission" name="OTA commission" fill="#DC2626" radius={[3, 3, 0, 0]} />
                </BarChart>
            </ChartCard>
            <TableView rows={data.rows} columns={[
                ["month","Month"],["reservations","Bookings"],["nights","Nights"],
                ["room_revenue","Room rev.","money"],["pos_revenue","POS rev.","money"],
                ["total_revenue","Total rev.","money"],["payments_received","Received","money"],
                ["refunds","Refunds","money"],["net_collected","Net collected","money"],
                ["estimated_ota_commission","OTA comm.","money"],
                ["net_revenue_after_commission","Net rev.","money"],
                ["cancellations","Cancel."],["no_shows","No-show"],
            ]} />
        </div>
    );
}
